/**
 * Utility for normalizing process status strings from SAMAI / CPNU / database.
 * SAMAI processes explicitly return "VIGENTE", "(SI", "(NO", "(SI)", "(NO)", "SI", "NO", etc.
 * CPNU processes do not have an explicit "VIGENTE" column and often return null / "NULL" / "N/A".
 */

export interface ProcessStatusInfo {
    isVigente: boolean;
    text: string;
    badgeClass: string;
    headerBadgeClass: string;
}

export function getVigenteStatusInfo(rawStatus?: string | null): ProcessStatusInfo {
    if (!rawStatus) {
        return {
            isVigente: true,
            text: "Vigente",
            badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
            headerBadgeClass: "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-emerald-500/20"
        };
    }

    // Strip parentheses, outer quotes, and extra whitespace: e.g. "(SI" -> "SI", "(NO)" -> "NO"
    const clean = String(rawStatus).replace(/[()"]/g, "").trim().toUpperCase();

    // CPNU / DB processes returning literal "NULL", "N/A", "NONE", "UNDEFINED" or empty string
    if (["NULL", "N/A", "NONE", "UNDEFINED", ""].includes(clean)) {
        return {
            isVigente: true,
            text: "Vigente",
            badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
            headerBadgeClass: "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-emerald-500/20"
        };
    }

    // SAMAI "Vigente: (NO)" or explicit inactive / closed / archived statuses
    if (["NO", "INACTIVO", "INACTIVE", "CERRADO", "CLOSED", "ARCHIVADO", "TERMINADO"].includes(clean)) {
        let text = "Inactivo";
        if (clean === "ARCHIVADO") text = "Archivado";
        else if (clean === "CERRADO" || clean === "CLOSED" || clean === "TERMINADO") text = "Cerrado";

        return {
            isVigente: false,
            text,
            badgeClass: "bg-red-50 text-red-700 border border-red-200",
            headerBadgeClass: "bg-gray-200 text-gray-700"
        };
    }

    // SAMAI "Vigente: (SI)", "VIGENTE" or active/open processes
    if (
        ["VIGENTE", "SI", "ACTIVO", "ACTIVE", "ABIERTO", "YES"].includes(clean) ||
        clean.startsWith("VIG") ||
        clean.startsWith("ACT")
    ) {
        return {
            isVigente: true,
            text: "Vigente",
            badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
            headerBadgeClass: "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-emerald-500/20"
        };
    }

    // Fallback for custom status strings
    return {
        isVigente: true,
        text: rawStatus.replace(/[()]/g, "").trim() || "Vigente",
        badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        headerBadgeClass: "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-emerald-500/20"
    };
}
